import { useMemo, useState, type ElementType, type ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { IgniteChakraModal } from "../../Components/Modal/IgniteChakraModal";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineClipboardList,
  HiOutlineDocumentText,
  HiOutlineLightningBolt,
  HiOutlineOfficeBuilding,
  HiOutlinePhotograph,
  HiOutlinePresentationChartLine,
  HiOutlineUsers,
} from "react-icons/hi";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { REPORT_TYPES } from "./measureInternalMockData";
import { DashboardDataSelect, DashboardSelectField } from "../../Components/DashboardSelect";
import {
  useAssignMeasureCohortOrganizationsMutation,
  useCreateMeasureCohortMutation,
  useGetFundersQuery,
  useGetMeasureDashboardQuery,
  useLazyGetMeasureExecutiveSummaryQuery,
  useLazyGetMeasureEngagementReportQuery,
  useLazyGetMeasureFunderReportQuery,
  useLazyGetMeasureImpactReportQuery,
  useLazyGetMeasureParticipationReportQuery,
  usePatchMeasureCohortMutation,
  useUnassignMeasureCohortOrganizationsMutation,
} from "../../../redux/reducers/apiSlice";
import type { MeasureDashboardQueryParams } from "../../../redux/reducers/types";
import {
  downloadMeasurePlatformReportCsv,
  type MeasurePlatformReport,
} from "./measurePlatformReportCsv";
import { downloadMeasurePlatformReportPdf } from "./measurePlatformReportPdf";
import { platformScrollbarSx } from "../../utils/platformScrollbar";

/** Radix Select forbids empty-string item values; use this sentinel for "no funder". */
const NO_LINKED_FUNDER = "__no_linked_funder__";

const exportUnavailable = (kind: string) => {
  toast(`${kind} export is not available yet.`, { icon: "📄" });
};

const reportIcons: Record<string, ElementType> = {
  funder: HiOutlinePresentationChartLine,
  exec: HiOutlineChartBar,
  engagement: HiOutlineLightningBolt,
  participation: HiOutlineClipboardList,
  impact: HiOutlinePhotograph,
};

const reportAccent: Record<string, string> = {
  funder: "#2b6cb0",
  exec: "#3182ce",
  engagement: "#4299e1",
  participation: "#63b3ed",
  impact: "#0987a0",
};

type StatProps = {
  label: string;
  value: string;
  icon: ElementType;
  hint?: string;
  unavailable?: boolean;
};

const StatTile = ({ label, value, icon, hint, unavailable }: StatProps) => (
  <Flex
    align="flex-start"
    gap={2.5}
    p={3}
    bg="white"
    borderRadius="lg"
    border="1px solid"
    borderColor={unavailable ? "orange.100" : "gray.100"}
    boxShadow="sm"
    transition="box-shadow 0.15s ease, border-color 0.15s ease"
    _hover={{ borderColor: "gray.200", boxShadow: "md" }}
  >
    <Flex
      align="center"
      justify="center"
      w={8}
      h={8}
      borderRadius="md"
      bg="blue.50"
      color="blue.600"
      flexShrink={0}
    >
      <Icon as={icon} boxSize={4} />
    </Flex>
    <Box minW={0}>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        {label}
      </Text>
      <Text fontSize="lg" fontWeight="semibold" color={unavailable ? "gray.400" : "gray.800"} lineHeight="shorter" mt={0.5}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color="gray.500" mt={0.5}>
          {hint}
        </Text>
      )}
    </Box>
  </Flex>
);

const SectionCard = ({
  title,
  description,
  action,
  flushBody,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  flushBody?: boolean;
  children: ReactNode;
}) => (
  <Box
    bg="white"
    borderRadius="xl"
    border="1px solid"
    borderColor="gray.100"
    boxShadow="sm"
    overflow="hidden"
    mb={5}
  >
    <Flex
      px={{ base: 3, md: 4 }}
      py={2.5}
      borderBottom="1px solid"
      borderColor="gray.100"
      align={{ base: "stretch", sm: "center" }}
      justify="space-between"
      direction={{ base: "column", sm: "row" }}
      gap={2}
      bg="gray.50"
    >
      <Box>
        <Heading as="h2" size="xs" color="gray.800" fontWeight="semibold" letterSpacing="tight">
          {title}
        </Heading>
        {description && (
          <Text fontSize="xs" color="gray.500" mt={0.5} maxW="lg" lineHeight="short">
            {description}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    <Box px={flushBody ? 0 : { base: 3, md: 4 }} py={flushBody ? 0 : 3}>
      {children}
    </Box>
  </Box>
);

const MeasureInternalDashboard = () => {
  const navigate = useNavigate();
  const [activeWindow, setActiveWindow] = useState<"30" | "60" | "90">("30");
  const [orgFilter, setOrgFilter] = useState("");
  const [funderFilter, setFunderFilter] = useState("All funders");
  const [geoFilter, setGeoFilter] = useState("All regions");
  const [engagementFilter, setEngagementFilter] = useState("All levels");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [showNeedsSupportOnly, setShowNeedsSupportOnly] = useState(false);
  const [cohortFilter, setCohortFilter] = useState("__all__");
  const assignModal = useDisclosure();
  const createCohortModal = useDisclosure();
  const reportModal = useDisclosure();
  const [generatedReport, setGeneratedReport] = useState<MeasurePlatformReport | null>(null);
  const [generatingReportId, setGeneratingReportId] = useState<string | null>(null);
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [assignOrgSearch, setAssignOrgSearch] = useState("");
  const [assignSelectedOrgIds, setAssignSelectedOrgIds] = useState<string[]>([]);
  const [newCohortName, setNewCohortName] = useState("");
  const [newCohortFunderId, setNewCohortFunderId] = useState(NO_LINKED_FUNDER);

  const [createMeasureCohort, { isLoading: createCohortLoading }] = useCreateMeasureCohortMutation();
  const [assignToCohort, { isLoading: assignSaving }] = useAssignMeasureCohortOrganizationsMutation();
  const [unassignFromCohort, { isLoading: unassignSaving }] = useUnassignMeasureCohortOrganizationsMutation();
  const [patchCohort] = usePatchMeasureCohortMutation();
  const { data: fundersData } = useGetFundersQuery();
  const [fetchExecutiveSummary] = useLazyGetMeasureExecutiveSummaryQuery();
  const [fetchFunderReport] = useLazyGetMeasureFunderReportQuery();
  const [fetchEngagementReport] = useLazyGetMeasureEngagementReportQuery();
  const [fetchParticipationReport] = useLazyGetMeasureParticipationReportQuery();
  const [fetchImpactReport] = useLazyGetMeasureImpactReportQuery();

  const queryParams = useMemo<MeasureDashboardQueryParams>(
    () => ({
      org: orgFilter.trim() || undefined,
      funder: funderFilter !== "All funders" ? funderFilter : undefined,
      geo: geoFilter !== "All regions" ? geoFilter : undefined,
      engagement: engagementFilter !== "All levels" ? engagementFilter : undefined,
      cohort: cohortFilter !== "__all__" ? cohortFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      inactiveOnly: showInactiveOnly || undefined,
      needsSupportOnly: showNeedsSupportOnly || undefined,
    }),
    [
      orgFilter,
      funderFilter,
      geoFilter,
      engagementFilter,
      cohortFilter,
      dateFrom,
      dateTo,
      showInactiveOnly,
      showNeedsSupportOnly,
    ]
  );

  const { data, isLoading, isFetching, isError, refetch } = useGetMeasureDashboardQuery(queryParams);

  const funderSelectOptions = useMemo(
    () => (data?.filters.funders ?? ["All funders"]).map((f) => ({ value: f, label: f })),
    [data?.filters.funders]
  );
  const geoSelectOptions = useMemo(
    () => (data?.filters.regions ?? ["All regions"]).map((g) => ({ value: g, label: g })),
    [data?.filters.regions]
  );
  const engagementSelectOptions = useMemo(
    () =>
      (data?.filters.engagementLevels ?? ["All levels", "Low", "Medium", "High"]).map((e) => ({
        value: e,
        label: e,
      })),
    [data?.filters.engagementLevels]
  );

  const metrics = data?.metrics;
  const organizations = data?.organizations ?? [];
  const funderPortfolios = data?.funderPortfolios ?? [];
  const cohortPortfolios = data?.cohortPortfolios ?? [];
  const cohortFilterOptions = data?.cohortFilterOptions ?? [
    { value: "__all__", label: "All cohorts" },
    { value: "unassigned", label: "Unassigned" },
  ];
  const capabilities = data?.capabilities;

  const activeUserDisplay =
    activeWindow === "30"
      ? metrics?.activeUsers30
      : activeWindow === "60"
        ? metrics?.activeUsers60
        : metrics?.activeUsers90;

  const cohortSelectOptionsForAssign = useMemo(
    () => cohortFilterOptions.filter((o) => o.value !== "__all__" && o.value !== "unassigned"),
    [cohortFilterOptions]
  );

  const openAssignModal = () => {
    setSelectedCohortId(cohortSelectOptionsForAssign[0]?.value ?? "");
    setAssignSelectedOrgIds([]);
    setAssignOrgSearch("");
    assignModal.onOpen();
  };

  const openCreateCohortModal = () => {
    setNewCohortName("");
    setNewCohortFunderId(NO_LINKED_FUNDER);
    createCohortModal.onOpen();
  };

  const createCohortFunderSelectOptions = useMemo(() => {
    const opts = [{ value: NO_LINKED_FUNDER, label: "No linked funder" }];
    if (Array.isArray(fundersData)) {
      for (const f of fundersData) {
        if (f?.id && f?.name) {
          opts.push({ value: f.id, label: f.name });
        }
      }
    }
    return opts;
  }, [fundersData]);

  const assignOrgList = useMemo(() => {
    const q = assignOrgSearch.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((o) => o.name.toLowerCase().includes(q));
  }, [organizations, assignOrgSearch]);

  const toggleAssignOrg = (id: string) => {
    setAssignSelectedOrgIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAssignSave = async () => {
    if (!selectedCohortId || assignSelectedOrgIds.length === 0) {
      toast.error("Select a cohort and at least one organization.");
      return;
    }
    try {
      await assignToCohort({ cohortId: selectedCohortId, organizationIds: assignSelectedOrgIds }).unwrap();
      await refetch();
      toast.success("Organizations assigned to cohort.");
      assignModal.onClose();
    } catch {
      toast.error("Could not assign organizations.");
    }
  };

  const handleUnassignSelected = async () => {
    if (assignSelectedOrgIds.length === 0) {
      toast.error("Select at least one organization.");
      return;
    }
    try {
      await unassignFromCohort({ organizationIds: assignSelectedOrgIds }).unwrap();
      await refetch();
      toast.success("Removed selected organizations from their cohorts.");
      assignModal.onClose();
    } catch {
      toast.error("Could not remove cohort assignments.");
    }
  };

  const handleCreateCohort = async () => {
    const name = newCohortName.trim();
    if (!name) {
      toast.error("Enter a cohort name.");
      return;
    }
    try {
      await createMeasureCohort({
        name,
        funderId: newCohortFunderId === NO_LINKED_FUNDER ? null : newCohortFunderId,
      }).unwrap();
      toast.success("Cohort created.");
      setNewCohortName("");
      setNewCohortFunderId(NO_LINKED_FUNDER);
      createCohortModal.onClose();
    } catch {
      toast.error("Could not create cohort.");
    }
  };

  const handleArchiveCohort = async (cohortId: string, label: string, memberCount: number) => {
    const memberLine =
      memberCount > 0
        ? `${memberCount} organization${memberCount === 1 ? "" : "s"} will be removed from this cohort (not deleted from Ignite).`
        : "This cohort has no assigned organizations.";
    if (!window.confirm(`Archive cohort “${label}”?\n\n${memberLine}`)) {
      return;
    }
    try {
      const result = await patchCohort({ cohortId, body: { status: "INACTIVE" } }).unwrap();
      await refetch();
      const removed = result.unassignedOrganizations ?? memberCount;
      if (removed > 0) {
        toast.success(`Cohort archived. ${removed} organization${removed === 1 ? "" : "s"} unassigned.`);
      } else {
        toast.success("Cohort archived.");
      }
    } catch {
      toast.error("Could not archive cohort.");
    }
  };

  const isReportTypeEnabled = (reportId: string) => {
    const types = capabilities?.reportTypes;
    if (types && reportId in types) {
      return types[reportId as keyof typeof types] === true;
    }
    return reportId === "exec" && capabilities?.reportGeneration === true;
  };

  const handleGenerateReport = async (reportId: string, openModal = true): Promise<MeasurePlatformReport | null> => {
    if (!isReportTypeEnabled(reportId)) {
      return null;
    }

    if (reportId === "funder" && funderFilter === "All funders") {
      toast.error("Select a funder in the filters above to generate a funder report.");
      return null;
    }

    setGeneratingReportId(reportId);
    try {
      let result: MeasurePlatformReport | null = null;
      if (reportId === "funder") {
        result = await fetchFunderReport(queryParams).unwrap();
      } else if (reportId === "exec") {
        result = await fetchExecutiveSummary(queryParams).unwrap();
      } else if (reportId === "engagement") {
        result = await fetchEngagementReport(queryParams).unwrap();
      } else if (reportId === "participation") {
        result = await fetchParticipationReport(queryParams).unwrap();
      } else if (reportId === "impact") {
        result = await fetchImpactReport(queryParams).unwrap();
      }
      if (result) {
        setGeneratedReport(result);
        if (openModal) {
          reportModal.onOpen();
        }
      }
      return result;
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === "object" &&
        "data" in err &&
        err.data &&
        typeof err.data === "object" &&
        "message" in err.data &&
        typeof err.data.message === "string"
          ? err.data.message
          : "Could not generate report.";
      toast.error(message);
      return null;
    } finally {
      setGeneratingReportId(null);
    }
  };

  const resetFilters = () => {
    setOrgFilter("");
    setFunderFilter("All funders");
    setGeoFilter("All regions");
    setEngagementFilter("All levels");
    setCohortFilter("__all__");
    setDateFrom("");
    setDateTo("");
    setShowInactiveOnly(false);
    setShowNeedsSupportOnly(false);
  };

  const statsLoading = isLoading || (isFetching && !data);

  return (
    <Box w="full" maxW="1280px" pb={4}>
      <Flex
        direction={{ base: "column", sm: "row" }}
        align={{ sm: "flex-start" }}
        justify="space-between"
        gap={3}
        mb={5}
      >
        <Box>
          <Text fontSize="10px" fontWeight="semibold" color="green.600" letterSpacing="0.08em" mb={1}>
            LIVE PLATFORM DATA
          </Text>
          <Heading as="h1" size="md" color="gray.900" fontWeight="semibold" lineHeight="snug" letterSpacing="-0.02em">
            Network health & engagement
          </Heading>
          <Text fontSize="sm" color="gray.500" mt={1} maxW="2xl" lineHeight="tall">
            Counts and organization signals from Ignite production data.
            {data?.generatedAt && (
              <Box as="span" display="block" mt={1} fontSize="xs" color="gray.400">
                Updated {new Date(data.generatedAt).toLocaleString()}
                {isFetching && !isLoading ? " · refreshing…" : ""}
              </Box>
            )}
          </Text>
        </Box>
        <Menu placement="bottom-end">
          <MenuButton
            as={Button}
            size="sm"
            colorScheme="blue"
            variant="outline"
            rightIcon={<ChevronDownIcon />}
            borderRadius="md"
            fontWeight="medium"
            isDisabled={!capabilities?.exports}
          >
            Export
          </MenuButton>
          <MenuList borderRadius="sm" shadow="md" py={1} fontSize="sm" minW="160px">
            <MenuItem borderRadius="sm" onClick={() => exportUnavailable("PDF")}>
              PDF snapshot
            </MenuItem>
            <MenuItem borderRadius="sm" onClick={() => exportUnavailable("CSV")}>
              CSV tables
            </MenuItem>
            <MenuItem borderRadius="sm" onClick={() => toast("Share links are not available yet.", { icon: "🔗" })}>
              Copy share link
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      {isError && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Could not load dashboard data.{" "}
            <Button size="xs" variant="link" colorScheme="red" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {data?.activeUserDefinition && (
        <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          <AlertDescription>{data.activeUserDefinition}</AlertDescription>
        </Alert>
      )}

      <SectionCard
        title="Scope & filters"
        description="Filters query live organization rows and activity in the selected date window."
        action={
          <Button size="sm" variant="ghost" colorScheme="gray" onClick={resetFilters}>
            Reset
          </Button>
        }
      >
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={3}>
          <GridItem>
            <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
              Organization
            </Text>
            <Input
              size="sm"
              borderRadius="sm"
              bg="white"
              border="1px solid"
              borderColor="blue.100"
              _focusVisible={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #63b3ed" }}
              placeholder="Search name or ID"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            />
          </GridItem>
          <GridItem>
            <DashboardSelectField
              label="Grant / funder"
              selectId="measure-filter-funder"
              value={funderFilter}
              onValueChange={setFunderFilter}
              options={funderSelectOptions}
            />
          </GridItem>
          <GridItem>
            <DashboardSelectField
              label="Geography"
              selectId="measure-filter-geo"
              value={geoFilter}
              onValueChange={setGeoFilter}
              options={geoSelectOptions}
            />
          </GridItem>
          <GridItem>
            <DashboardSelectField
              label="Engagement"
              selectId="measure-filter-engagement"
              value={engagementFilter}
              onValueChange={setEngagementFilter}
              options={engagementSelectOptions}
            />
          </GridItem>
          <GridItem>
            <DashboardSelectField
              label="Cohort"
              selectId="measure-filter-cohort"
              value={cohortFilter}
              onValueChange={setCohortFilter}
              options={cohortFilterOptions}
            />
          </GridItem>
          <GridItem colSpan={{ base: 1, xl: 2 }}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>
              Activity window (for engagement signals)
            </Text>
            <HStack spacing={2}>
              <Input
                type="date"
                size="sm"
                borderRadius="sm"
                bg="white"
                border="1px solid"
                borderColor="blue.100"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <Text fontSize="xs" color="gray.400">
                –
              </Text>
              <Input
                type="date"
                size="sm"
                borderRadius="sm"
                bg="white"
                border="1px solid"
                borderColor="blue.100"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </HStack>
          </GridItem>
        </Grid>
        <HStack spacing={2} flexWrap="wrap" mt={4}>
          <Button
            size="sm"
            borderRadius="sm"
            variant={showInactiveOnly ? "solid" : "outline"}
            colorScheme="orange"
            onClick={() => setShowInactiveOnly((v) => !v)}
          >
            Inactive only
          </Button>
          <Button
            size="sm"
            borderRadius="sm"
            variant={showNeedsSupportOnly ? "solid" : "outline"}
            colorScheme="red"
            onClick={() => setShowNeedsSupportOnly((v) => !v)}
          >
            Needs support
          </Button>
        </HStack>
      </SectionCard>

      {statsLoading ? (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={5}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} height="84px" borderRadius="md" startColor="blue.50" endColor="white" />
          ))}
        </SimpleGrid>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={2}>
          <StatTile
            label="Organizations"
            value={(metrics?.totalOrganizations ?? 0).toLocaleString()}
            icon={HiOutlineOfficeBuilding}
          />
          <StatTile
            label="Registered users"
            value={(metrics?.totalUsers ?? 0).toLocaleString()}
            icon={HiOutlineUsers}
            hint="Excludes platform operators"
          />
          <GridItem colSpan={{ base: 1, sm: 2, lg: 1 }}>
            <Box
              p={3}
              bg="white"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.100"
              boxShadow="sm"
              h="full"
            >
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Active users
                </Text>
                <ButtonGroup size="xs" isAttached variant="outline" borderRadius="sm">
                  {(["30", "60", "90"] as const).map((w) => (
                    <Button
                      key={w}
                      borderRadius="sm"
                      variant={activeWindow === w ? "solid" : "outline"}
                      colorScheme={activeWindow === w ? "blue" : "gray"}
                      onClick={() => setActiveWindow(w)}
                    >
                      {w}d
                    </Button>
                  ))}
                </ButtonGroup>
              </Flex>
              <Text fontSize="xl" fontWeight="semibold" color="gray.800">
                {(activeUserDisplay ?? 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                {(metrics?.activeUsers30 ?? 0).toLocaleString()} / {(metrics?.activeUsers60 ?? 0).toLocaleString()} /{" "}
                {(metrics?.activeUsers90 ?? 0).toLocaleString()}{" "}
                <Box as="span" color="gray.400">
                  (30d · 60d · 90d)
                </Box>
              </Text>
            </Box>
          </GridItem>
          <StatTile
            label="New this month"
            value={(metrics?.newUsersThisMonth ?? 0).toLocaleString()}
            icon={HiOutlineCalendar}
            hint="Member registrations"
          />
          <StatTile
            label="Posts & interactions"
            value={(metrics?.totalPostsInteractions ?? 0).toLocaleString()}
            icon={HiOutlineLightningBolt}
            hint={`${(metrics?.totalPosts ?? 0).toLocaleString()} posts · ${(metrics?.totalInteractions ?? 0).toLocaleString()} reactions`}
          />
          <StatTile
            label="Reports generated"
            value={(metrics?.reportsGenerated ?? 0).toLocaleString()}
            icon={HiOutlineDocumentText}
          />
          <StatTile
            label="Events & gatherings"
            value={
              capabilities?.eventsGatherings
                ? (metrics?.eventsGatherings ?? 0).toLocaleString()
                : "Not tracked"
            }
            icon={HiOutlineClipboardList}
            hint={
              capabilities?.eventsGatherings
                ? "Logged by organizations across Ignite"
                : "Events module unavailable"
            }
            unavailable={!capabilities?.eventsGatherings}
          />
        </SimpleGrid>
      )}

      <SectionCard
        flushBody
        title="Organizations"
        description={`${organizations.length.toLocaleString()} organization${organizations.length === 1 ? "" : "s"} match current filters.`}
      >
        <TableContainer overflowX="auto" sx={platformScrollbarSx}>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th
                  color="gray.500"
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="none"
                  border="none"
                  pb={3}
                  pl={{ base: 3, md: 4 }}
                >
                  Organization
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Region
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Onboarding
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Engagement
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Funder
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Cohort
                </Th>
                <Th color="gray.500" fontSize="xs" fontWeight="semibold" textTransform="none" border="none" pb={3}>
                  Signals
                </Th>
                <Th w="44px" border="none" pb={3} pr={{ base: 3, md: 4 }} />
              </Tr>
            </Thead>
            <Tbody>
              {statsLoading ? (
                <Tr>
                  <Td colSpan={8} py={8} border="none">
                    <Skeleton height="120px" borderRadius="md" />
                  </Td>
                </Tr>
              ) : organizations.length === 0 ? (
                <Tr>
                  <Td colSpan={8} py={10} border="none">
                    <VStack spacing={2}>
                      <Text color="gray.600" fontWeight="medium">
                        Nothing matches these filters
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Try clearing search or toggling quick filters.
                      </Text>
                      <Button size="sm" variant="link" colorScheme="blue" onClick={resetFilters}>
                        Reset filters
                      </Button>
                    </VStack>
                  </Td>
                </Tr>
              ) : (
                organizations.map((row) => (
                  <Tr
                    key={row.id}
                    _hover={{ bg: "gray.50" }}
                    sx={{ "td, th": { borderColor: "gray.100" } }}
                  >
                    <Td fontWeight="medium" color="gray.800" py={2.5} fontSize="sm" pl={{ base: 3, md: 4 }}>
                      <Link to={`/organizations/${row.id}/profile`} style={{ color: "inherit" }}>
                        {row.name}
                      </Link>
                    </Td>
                    <Td color="gray.600" py={2.5} fontSize="sm">
                      {row.region}
                    </Td>
                    <Td py={2.5}>
                      <Badge
                        px={1.5}
                        py={0}
                        borderRadius="sm"
                        fontSize="0.65rem"
                        fontWeight="medium"
                        colorScheme={row.onboarding === "Complete" ? "green" : row.onboarding === "In progress" ? "yellow" : "gray"}
                      >
                        {row.onboarding}
                      </Badge>
                    </Td>
                    <Td py={2.5}>
                      <Badge
                        px={1.5}
                        py={0}
                        borderRadius="sm"
                        fontSize="0.65rem"
                        fontWeight="medium"
                        colorScheme={row.engagement === "High" ? "green" : row.engagement === "Medium" ? "blue" : "orange"}
                      >
                        {row.engagement}
                      </Badge>
                    </Td>
                    <Td fontSize="xs" color="gray.600" maxW="140px" isTruncated py={2.5}>
                      {row.funder}
                    </Td>
                    <Td fontSize="xs" color="gray.600" py={2.5}>
                      {row.cohort ? (
                        <Text as="span">{row.cohort}</Text>
                      ) : (
                        <Text as="span" color="gray.400">
                          —
                        </Text>
                      )}
                    </Td>
                    <Td py={2.5}>
                      <HStack spacing={1} flexWrap="wrap">
                        {row.inactive && (
                          <Badge borderRadius="sm" colorScheme="orange" variant="subtle" fontSize="0.6rem">
                            Inactive
                          </Badge>
                        )}
                        {row.needsSupport && (
                          <Badge borderRadius="sm" colorScheme="red" variant="subtle" fontSize="0.6rem">
                            Support
                          </Badge>
                        )}
                        {!row.inactive && !row.needsSupport && (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </HStack>
                    </Td>
                    <Td py={2.5} pr={{ base: 3, md: 4 }}>
                      <IconButton
                        aria-label="Open organization"
                        size="xs"
                        variant="ghost"
                        borderRadius="sm"
                        icon={<Icon as={BsThreeDotsVertical} />}
                        onClick={() => navigate(`/organizations/${row.id}/profile`)}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </SectionCard>

      <SectionCard
        title="Cohorts & funders"
        description="Operator-defined cohorts (linked catalog funder optional) and live participation by report funder."
        action={
          <HStack spacing={2}>
            <Button size="sm" variant="outline" colorScheme="blue" borderRadius="sm" onClick={openCreateCohortModal}>
              New cohort
            </Button>
            <Button size="sm" colorScheme="blue" borderRadius="sm" onClick={openAssignModal}>
              Assign organizations
            </Button>
          </HStack>
        }
      >
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
          <Box>
            <HStack mb={3} spacing={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                Cohort directory
              </Text>
            </HStack>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100">
                      Cohort
                    </Th>
                    <Th textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100">
                      Funder
                    </Th>
                    <Th isNumeric textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100">
                      Orgs
                    </Th>
                    <Th textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100" w="44px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {cohortPortfolios.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} fontSize="sm" color="gray.500" borderColor="gray.100">
                        No cohorts yet. Create one to start grouping organizations.
                      </Td>
                    </Tr>
                  ) : (
                    cohortPortfolios.map((c) => (
                      <Tr key={c.id} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="semibold" borderColor="gray.100" fontSize="sm">
                          {c.name}
                        </Td>
                        <Td fontSize="xs" color="gray.600" borderColor="gray.100">
                          {c.funder}
                        </Td>
                        <Td isNumeric sx={{ fontVariantNumeric: "tabular-nums" }} borderColor="gray.100">
                          {c.orgCount}
                        </Td>
                        <Td borderColor="gray.100" textAlign="right">
                          <Menu placement="left-start">
                            <MenuButton
                              as={IconButton}
                              aria-label="Cohort actions"
                              icon={<Icon as={BsThreeDotsVertical} />}
                              size="xs"
                              variant="ghost"
                              borderRadius="sm"
                            />
                            <MenuList borderRadius="sm" fontSize="sm" py={1}>
                              <MenuItem
                                borderRadius="sm"
                                onClick={() => handleArchiveCohort(c.id, c.name, c.orgCount)}
                              >
                                Archive cohort
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          <Box>
            <HStack mb={3} spacing={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                Participation index (by engagement)
              </Text>
            </HStack>
            <VStack align="stretch" spacing={3}>
              {cohortPortfolios.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  Participation bars appear once cohorts exist and include organizations.
                </Text>
              ) : (
                cohortPortfolios.map((c) => (
                  <Box key={c.id}>
                    <Flex justify="space-between" mb={1} fontSize="xs">
                      <Text fontWeight="medium" color="gray.700">
                        {c.name}
                      </Text>
                      <Text color="gray.500" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {c.participationIndex}%
                      </Text>
                    </Flex>
                    <Box h="1.5" bg="blue.100" borderRadius="sm" overflow="hidden">
                      <Box
                        h="full"
                        w={`${c.participationIndex}%`}
                        bg="blue.400"
                        borderRadius="sm"
                        transition="width 0.3s ease"
                      />
                    </Box>
                  </Box>
                ))
              )}
            </VStack>
            <Text fontSize="xs" color="gray.400" mt={4}>
              Index = share of member orgs with medium or high engagement in the activity window.
            </Text>
          </Box>
        </Grid>

        <Box mt={6} pt={5} borderTop="1px solid" borderColor="gray.100">
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={3}>
            Live — organizations by report funder
          </Text>
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100">
                      Funder
                    </Th>
                    <Th isNumeric textTransform="none" color="gray.500" fontSize="xs" borderColor="gray.100">
                      Orgs
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {funderPortfolios.length === 0 ? (
                    <Tr>
                      <Td colSpan={2} fontSize="sm" color="gray.500" borderColor="gray.100">
                        No funder-linked organizations yet.
                      </Td>
                    </Tr>
                  ) : (
                    funderPortfolios.map((c) => (
                      <Tr key={c.id} _hover={{ bg: "gray.50" }}>
                        <Td fontWeight="semibold" borderColor="gray.100" fontSize="sm">
                          {c.funder}
                        </Td>
                        <Td isNumeric sx={{ fontVariantNumeric: "tabular-nums" }} borderColor="gray.100">
                          {c.orgCount}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
            <VStack align="stretch" spacing={3}>
              {funderPortfolios.map((c) => (
                <Box key={c.id}>
                  <Flex justify="space-between" mb={1} fontSize="xs">
                    <Text fontWeight="medium" color="gray.700">
                      {c.funder}
                    </Text>
                    <Text color="gray.500" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {c.participationIndex}%
                    </Text>
                  </Flex>
                  <Box h="1.5" bg="blue.100" borderRadius="sm" overflow="hidden">
                    <Box
                      h="full"
                      w={`${c.participationIndex}%`}
                      bg="teal.400"
                      borderRadius="sm"
                      transition="width 0.3s ease"
                    />
                  </Box>
                </Box>
              ))}
            </VStack>
          </Grid>
        </Box>
      </SectionCard>

      <SectionCard
        title="Reporting templates"
        description="All report templates use live dashboard filters. Funder reports require a funder filter."
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
          {REPORT_TYPES.map((r) => {
            const Ri = reportIcons[r.id] ?? HiOutlineDocumentText;
            const stripe = reportAccent[r.id] ?? "#3182ce";
            return (
              <Flex
                key={r.id}
                direction="column"
                p={3}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.100"
                bg="white"
                boxShadow="sm"
                position="relative"
                overflow="hidden"
                transition="border-color 0.15s ease, box-shadow 0.15s ease"
                _hover={{ borderColor: "gray.200", boxShadow: "md" }}
              >
                <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={stripe} />
                <HStack spacing={2.5} pl={2} mb={2.5} align="flex-start">
                  <Flex w={8} h={8} borderRadius="sm" bg="blue.50" align="center" justify="center" color="blue.600">
                    <Icon as={Ri} boxSize={4} />
                  </Flex>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" fontSize="sm">
                      {r.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="short">
                      {r.description}
                    </Text>
                  </Box>
                </HStack>
                <Box mt="auto" pl={2}>
                  <Button
                    size="xs"
                    colorScheme="blue"
                    borderRadius="sm"
                    isDisabled={!isReportTypeEnabled(r.id)}
                    isLoading={generatingReportId === r.id}
                    onClick={() => handleGenerateReport(r.id)}
                  >
                    Generate
                  </Button>
                </Box>
              </Flex>
            );
          })}
        </SimpleGrid>
      </SectionCard>

      <IgniteChakraModal isOpen={assignModal.isOpen} onClose={assignModal.onClose} size="lg">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="md" mx={3} border="1px solid" borderColor="blue.100">
          <ModalHeader fontSize="md" fontWeight="semibold" pb={1}>
            Assign organizations to cohort
          </ModalHeader>
          <ModalCloseButton borderRadius="sm" />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Each organization can belong to at most one cohort. Assigning moves selected organizations into the cohort you pick below.
            </Text>
            <Text as="label" htmlFor="measure-assign-cohort" fontSize="xs" fontWeight="medium" color="gray.600" mb={1} display="block">
              Cohort
            </Text>
            <Box mb={3}>
              <DashboardDataSelect
                value={selectedCohortId}
                onValueChange={setSelectedCohortId}
                options={cohortSelectOptionsForAssign}
                placeholder={cohortSelectOptionsForAssign.length === 0 ? "Create a cohort first" : undefined}
                disabled={cohortSelectOptionsForAssign.length === 0}
                triggerId="measure-assign-cohort"
              />
            </Box>
            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
              Organizations
            </Text>
            <Input
              placeholder="Filter…"
              mb={2}
              size="sm"
              borderRadius="sm"
              borderColor="blue.100"
              value={assignOrgSearch}
              onChange={(e) => setAssignOrgSearch(e.target.value)}
            />
            <VStack align="stretch" maxH="240px" overflowY="auto" spacing={1.5}>
              {assignOrgList.map((o) => (
                <Flex
                  key={o.id}
                  justify="space-between"
                  align="center"
                  gap={2}
                  p={2}
                  borderRadius="sm"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <Checkbox
                    isChecked={assignSelectedOrgIds.includes(o.id)}
                    onChange={() => toggleAssignOrg(o.id)}
                    flexShrink={0}
                  >
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      {o.name}
                    </Text>
                  </Checkbox>
                  {o.cohort ? (
                    <Badge colorScheme="gray" variant="subtle" borderRadius="sm" fontSize="0.6rem" maxW="120px" isTruncated>
                      {o.cohort}
                    </Badge>
                  ) : null}
                </Flex>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} pt={2} flexWrap="wrap">
            <Button variant="ghost" size="sm" borderRadius="sm" onClick={assignModal.onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              colorScheme="gray"
              size="sm"
              borderRadius="sm"
              onClick={handleUnassignSelected}
              isLoading={unassignSaving}
              isDisabled={assignSelectedOrgIds.length === 0}
            >
              Remove from cohort
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              borderRadius="sm"
              onClick={handleAssignSave}
              isLoading={assignSaving}
              isDisabled={!selectedCohortId || assignSelectedOrgIds.length === 0}
            >
              Save assignments
            </Button>
          </ModalFooter>
        </ModalContent>
      </IgniteChakraModal>

      <IgniteChakraModal isOpen={createCohortModal.isOpen} onClose={createCohortModal.onClose} size="md">
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="md" mx={3} border="1px solid" borderColor="blue.100">
          <ModalHeader fontSize="md" fontWeight="semibold" pb={1}>
            New cohort
          </ModalHeader>
          <ModalCloseButton borderRadius="sm" />
          <ModalBody>
            <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
              Name
            </Text>
            <Input
              mb={3}
              size="sm"
              borderRadius="sm"
              borderColor="blue.100"
              value={newCohortName}
              onChange={(e) => setNewCohortName(e.target.value)}
              placeholder="e.g. 2025 Community Resilience grantees"
            />
            <DashboardSelectField
              label="Linked catalog funder (optional)"
              selectId="measure-new-cohort-funder"
              value={newCohortFunderId}
              onValueChange={setNewCohortFunderId}
              options={createCohortFunderSelectOptions}
            />
          </ModalBody>
          <ModalFooter gap={2} pt={2}>
            <Button variant="ghost" size="sm" borderRadius="sm" onClick={createCohortModal.onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              borderRadius="sm"
              onClick={handleCreateCohort}
              isLoading={createCohortLoading}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </IgniteChakraModal>

      <IgniteChakraModal
        isOpen={reportModal.isOpen}
        onClose={reportModal.onClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="md" mx={3} border="1px solid" borderColor="blue.100" maxH="90vh">
          <ModalHeader fontSize="md" fontWeight="semibold" pb={1}>
            {generatedReport?.title ?? "Platform report"}
          </ModalHeader>
          <ModalCloseButton borderRadius="sm" />
          <ModalBody>
            {generatedReport ? (
              <VStack align="stretch" spacing={3} fontSize="sm">
                <Text fontSize="xs" color="gray.500">
                  {generatedReport.scope.description}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  Activity window:{" "}
                  {new Date(generatedReport.activityWindow.from).toLocaleDateString()} –{" "}
                  {new Date(generatedReport.activityWindow.to).toLocaleDateString()}
                </Text>

                {generatedReport.reportType === "engagement" ? (
                  <>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Platform reach
                      </Text>
                      <SimpleGrid columns={2} spacing={2} fontSize="xs">
                        <Text color="gray.600">Active users (30 / 60 / 90d)</Text>
                        <Text fontWeight="medium">
                          {generatedReport.platformReach.activeUsers30} /{" "}
                          {generatedReport.platformReach.activeUsers60} /{" "}
                          {generatedReport.platformReach.activeUsers90}
                        </Text>
                        <Text color="gray.600">Posts & interactions (all time)</Text>
                        <Text fontWeight="medium">
                          {generatedReport.platformReach.totalPosts} /{" "}
                          {generatedReport.platformReach.totalInteractions}
                        </Text>
                      </SimpleGrid>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Portfolio in scope ({generatedReport.portfolio.organizationCount} orgs)
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Participation {generatedReport.portfolio.participationIndex}% · Posts in window{" "}
                        {generatedReport.portfolio.postsInWindow} · Report activity{" "}
                        {generatedReport.portfolio.orgReportsInWindow} · High{" "}
                        {generatedReport.portfolio.engagement.High ?? 0} · Medium{" "}
                        {generatedReport.portfolio.engagement.Medium ?? 0} · Low{" "}
                        {generatedReport.portfolio.engagement.Low ?? 0}
                      </Text>
                    </Box>
                    <Text fontSize="xs" color="gray.500">
                      {generatedReport.methodology}
                    </Text>
                    {generatedReport.byRegion.length > 0 ? (
                      <Box>
                        <Text fontWeight="semibold" color="gray.700" mb={1}>
                          By region (top {Math.min(5, generatedReport.byRegion.length)})
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          {generatedReport.byRegion.slice(0, 5).map((r) => (
                            <Text key={r.label} fontSize="xs" color="gray.600">
                              {r.label}: {r.orgCount} orgs · {r.participationIndex}% participation
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    ) : null}
                  </>
                ) : generatedReport.reportType === "executive-summary" ? (
                  <>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Platform snapshot
                      </Text>
                      <SimpleGrid columns={2} spacing={2} fontSize="xs">
                        <Text color="gray.600">Organizations (platform)</Text>
                        <Text fontWeight="medium">{generatedReport.platformSnapshot.totalOrganizations}</Text>
                        <Text color="gray.600">Users</Text>
                        <Text fontWeight="medium">{generatedReport.platformSnapshot.totalUsers}</Text>
                        <Text color="gray.600">Active users (30 / 60 / 90d)</Text>
                        <Text fontWeight="medium">
                          {generatedReport.platformSnapshot.activeUsers30} /{" "}
                          {generatedReport.platformSnapshot.activeUsers60} /{" "}
                          {generatedReport.platformSnapshot.activeUsers90}
                        </Text>
                        <Text color="gray.600">Impact reports (all time)</Text>
                        <Text fontWeight="medium">{generatedReport.platformSnapshot.reportsGenerated}</Text>
                      </SimpleGrid>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Portfolio in scope ({generatedReport.portfolio.organizationCount} orgs)
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Participation {generatedReport.portfolio.participationIndex}% · Inactive{" "}
                        {generatedReport.portfolio.inactive} · Needs support{" "}
                        {generatedReport.portfolio.needsSupport}
                      </Text>
                    </Box>
                    {generatedReport.funder.selectedFunder ? (
                      <Box p={2} borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.100">
                        <Text fontWeight="semibold" color="gray.800" fontSize="sm" mb={1}>
                          Funder — {generatedReport.funder.selectedFunder}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Impact reports filed to this funder in window:{" "}
                          {generatedReport.funder.impactReportsInWindow ?? 0}
                        </Text>
                        {generatedReport.funder.portfolioParticipation ? (
                          <Text fontSize="xs" color="gray.600" mt={0.5}>
                            {generatedReport.funder.portfolioParticipation.orgCount} organizations on platform ·{" "}
                            {generatedReport.funder.portfolioParticipation.participationIndex}% participation
                          </Text>
                        ) : null}
                      </Box>
                    ) : (
                      <Text fontSize="xs" color="gray.500">
                        {generatedReport.funder.note}
                      </Text>
                    )}
                  </>
                ) : generatedReport.reportType === "participation" ? (
                  <>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Participation totals
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {generatedReport.platformTotals.eventsInWindow} events in window ·{" "}
                        {generatedReport.platformTotals.totalMembersInScope} members · Participation{" "}
                        {generatedReport.portfolio.participationIndex}%
                      </Text>
                    </Box>
                    {generatedReport.recentEvents.length > 0 ? (
                      <Box>
                        <Text fontWeight="semibold" color="gray.700" mb={1}>
                          Recent events
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          {generatedReport.recentEvents.slice(0, 5).map((e) => (
                            <Text key={e.id} fontSize="xs" color="gray.600">
                              {e.title} ({e.eventType}) · {e.organizationName}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    ) : null}
                  </>
                ) : generatedReport.reportType === "impact" ? (
                  <>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Impact in window
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {generatedReport.impactReportsInWindow} impact reports ·{" "}
                        {generatedReport.portfolio.organizationCount} organizations in scope
                      </Text>
                    </Box>
                    {generatedReport.highlights.length > 0 ? (
                      <Box>
                        <Text fontWeight="semibold" color="gray.700" mb={1}>
                          Highlights
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {generatedReport.highlights.slice(0, 3).map((h) => (
                            <Box
                              key={h.id}
                              p={2}
                              borderRadius="md"
                              bg="gray.50"
                              border="1px solid"
                              borderColor="gray.100"
                            >
                              <Text fontSize="xs" fontWeight="semibold" color="gray.800">
                                {h.organizationName}
                              </Text>
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {h.funderName}
                                {h.grantName ? ` · ${h.grantName}` : ""}
                              </Text>
                              {h.narrativeExcerpt ? (
                                <Text fontSize="xs" color="gray.600" mt={1} noOfLines={3}>
                                  {h.narrativeExcerpt}
                                </Text>
                              ) : null}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    ) : (
                      <Text fontSize="xs" color="gray.500">
                        No impact reports in this window for the current filters.
                      </Text>
                    )}
                  </>
                ) : generatedReport.reportType === "funder" ? (
                  <>
                    <Box p={2} borderRadius="md" bg="blue.50" border="1px solid" borderColor="blue.100">
                      <Text fontWeight="semibold" color="gray.800" mb={1}>
                        {generatedReport.funder.name}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {generatedReport.funder.organizationsOnPlatform} organizations on platform ·{" "}
                        {generatedReport.funder.platformParticipationIndex}% participation ·{" "}
                        {generatedReport.funder.impactReportsInWindow} impact reports in window
                      </Text>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>
                        Filtered portfolio ({generatedReport.portfolioInScope.organizationCount} orgs)
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Participation {generatedReport.portfolioInScope.participationIndex}%
                      </Text>
                    </Box>
                  </>
                ) : null}

                <Box>
                  <Text fontWeight="semibold" color="gray.700" mb={1}>
                    Organizations ({generatedReport.organizations.length})
                  </Text>
                  <TableContainer maxH="220px" overflowY="auto" border="1px solid" borderColor="gray.100" borderRadius="md">
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                        <Tr>
                          <Th fontSize="xs">Name</Th>
                          <Th fontSize="xs">Engagement</Th>
                          <Th fontSize="xs">
                            {generatedReport.reportType === "engagement"
                              ? "Posts / reports"
                              : generatedReport.reportType === "participation"
                                ? "Members / events"
                                : generatedReport.reportType === "impact"
                                  ? "Posts / reports"
                                  : "Onboarding"}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {generatedReport.organizations.map((o) => (
                          <Tr key={o.id}>
                            <Td fontSize="xs">{o.name}</Td>
                            <Td fontSize="xs">{o.engagement}</Td>
                            <Td fontSize="xs">
                              {generatedReport.reportType === "engagement" ||
                              generatedReport.reportType === "impact"
                                ? `${o.postsInWindow} / ${o.reportsInWindow}`
                                : generatedReport.reportType === "participation"
                                  ? `${"memberCount" in o ? o.memberCount : 0} / ${"eventsInWindow" in o ? o.eventsInWindow : 0}`
                                  : o.onboarding}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter gap={2} pt={2}>
            <Button variant="ghost" size="sm" borderRadius="sm" onClick={reportModal.onClose}>
              Close
            </Button>
            {generatedReport ? (
              <>
                <Button
                  variant="outline"
                  colorScheme="blue"
                  size="sm"
                  borderRadius="sm"
                  onClick={() => {
                    try {
                      downloadMeasurePlatformReportPdf(generatedReport);
                      toast.success("PDF downloaded.");
                    } catch {
                      toast.error("Could not download PDF.");
                    }
                  }}
                >
                  Download PDF
                </Button>
                <Button
                  colorScheme="blue"
                  size="sm"
                  borderRadius="sm"
                  onClick={() => {
                    downloadMeasurePlatformReportCsv(generatedReport);
                    toast.success("CSV downloaded.");
                  }}
                >
                  Download CSV
                </Button>
              </>
            ) : null}
          </ModalFooter>
        </ModalContent>
      </IgniteChakraModal>
    </Box>
  );
};

export default MeasureInternalDashboard;
